// src/pages/Boutique.jsx — Boutique Admin (produits, promotions, statistiques — contrat réel)
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Package,
  Plus,
  Search,
  Eye,
  LayoutDashboard,
  Ticket,
  BarChart3,
  CheckCircle2,
  X,
  Pencil,
  Trash2,
  RefreshCw,
  ShieldAlert,
} from 'lucide-react';
import { useI18n } from '../i18n/useI18n';
import {
  PageHeader,
  StatusBadge,
  DonutChart,
  HorizontalBars,
  EmptyState,
} from '../components/ui/UIComponents';
import * as boutiqueApi from '../services/adminBoutique';
import { authService } from '../services/authService';

const TABS = [
  { id: 'overview', label: "Vue d'ensemble", icon: LayoutDashboard },
  { id: 'products', label: 'Produits', icon: Package },
  { id: 'create', label: 'Nouveau produit', icon: Plus },
  { id: 'promos', label: 'Codes promo', icon: Ticket },
  { id: 'stats', label: 'Statistiques', icon: BarChart3 },
];

const CHART_COLORS = ['#4f46e5', '#10b981', '#f59e0b', '#f43f5e', '#0ea5e9', '#8b5cf6'];

function canModifyProducts() {
  const user = authService.getCurrentUser();
  const n = user?.niveau;
  if (n === 1 || n === '1' || (typeof n === 'string' && String(n).toUpperCase() === 'SUPER_ADMIN')) {
    return true;
  }
  const perms = authService.getPermissions() || [];
  return perms.some((p) => {
    const code = typeof p === 'string' ? p : p?.code || p?.permission || '';
    return code === 'BOUTIQUE_PRODUITS_MODIFIER';
  });
}

function canModifyPromos() {
  const user = authService.getCurrentUser();
  const n = user?.niveau;
  if (n === 1 || n === '1' || (typeof n === 'string' && String(n).toUpperCase() === 'SUPER_ADMIN')) {
    return true;
  }
  const perms = authService.getPermissions() || [];
  return perms.some((p) => {
    const code = typeof p === 'string' ? p : p?.code || p?.permission || '';
    return code === 'BOUTIQUE_PROMOS_MODIFIER';
  });
}

function extractPromos(res) {
  if (!res) return [];
  if (Array.isArray(res)) return res;
  if (Array.isArray(res.promos)) return res.promos;
  if (Array.isArray(res.items)) return res.items;
  if (Array.isArray(res.data)) return res.data;
  return [];
}

function promoIsActive(p) {
  if (typeof p?.active === 'boolean') return p.active;
  const s = String(p?.status || '').toLowerCase();
  return s === 'active' || s === 'actif';
}

function promoUses(p) {
  return p?.uses ?? p?.useCount ?? p?.used ?? null;
}

function promoMaxUses(p) {
  return p?.maxUses ?? p?.max_uses ?? null;
}

function extractProducts(res) {
  if (!res) return [];
  if (Array.isArray(res)) return res;
  if (Array.isArray(res.products)) return res.products;
  if (Array.isArray(res.items)) return res.items;
  if (Array.isArray(res.data)) return res.data;
  return [];
}

function productTitle(p) {
  return p?.title ?? p?.name ?? '—';
}

function productPrice(p) {
  const v = p?.priceDsi ?? p?.price;
  return typeof v === 'number' ? v : Number(v) || 0;
}

function productStatus(p) {
  return p?.status ?? p?.editorial_status ?? '';
}

function productCreated(p) {
  return p?.createdAt ?? p?.created_at ?? p?.created ?? '—';
}

function humanError(err) {
  const raw = err?.body?.error || err?.body?.erreur || err?.body?.code || err?.message || '';
  const code = String(raw);
  if (err?.status === 401) return 'Session expirée. Veuillez vous reconnecter.';
  if (err?.status === 403) return 'Vous n’avez pas l’autorisation d’effectuer cette action.';
  if (err?.status === 404) return 'Produit introuvable.';
  if (err?.status === 502) return 'Le service boutique est temporairement injoignable.';
  if (err?.status === 503) return 'Le service boutique n’est pas configuré.';
  if (err?.status === 409 || err?.status === 422) {
    return code && !code.startsWith('Erreur') ? code : 'Opération refusée.';
  }
  if (err?.status === 400) {
    return code && !code.startsWith('Erreur') ? code : 'Requête invalide.';
  }
  return code || 'Une erreur est survenue.';
}

function productStatusBadge(status) {
  const map = {
    published: { key: 'ACTIF', label: 'Publié' },
    draft: { key: 'EN_ATTENTE', label: 'Brouillon' },
    archived: { key: 'INACTIF', label: 'Archivé' },
    active: { key: 'ACTIF', label: 'Actif' },
    inactive: { key: 'INACTIF', label: 'Inactif' },
  };
  const cfg = map[status] || { key: 'INACTIF', label: status || '—' };
  return <StatusBadge statusKey={cfg.key} text={cfg.label} />;
}

function orderStatusBadge(status) {
  const map = {
    paid: { key: 'ACTIF', label: 'Payée' },
    pending_payment: { key: 'EN_ATTENTE', label: 'En attente' },
    failed: { key: 'ERREUR', label: 'Échouée' },
    refunded: { key: 'EXPIRE', label: 'Remboursée' },
  };
  const cfg = map[status] || { key: 'INACTIF', label: status };
  return <StatusBadge statusKey={cfg.key} text={cfg.label} />;
}

export default function Boutique() {
  const { t } = useI18n();
  const [tab, setTab] = useState('overview');
  const [viewMode, setViewMode] = useState('cards');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [categoryFilter, setCategoryFilter] = useState('ALL');
  const [selectedProduct, setSelectedProduct] = useState(null);

  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(true);
  const [productsError, setProductsError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState(null);
  const [formSuccess, setFormSuccess] = useState(null);

  const [createForm, setCreateForm] = useState({ title: '', category: '', priceDsi: '' });
  const [editForm, setEditForm] = useState({ title: '', category: '', priceDsi: '', status: '' });
  const [editing, setEditing] = useState(false);

  const writeOk = canModifyProducts();
  const promosWriteOk = canModifyPromos();

  const [promos, setPromos] = useState([]);
  const [promosLoading, setPromosLoading] = useState(false);
  const [promosError, setPromosError] = useState(null);
  const [promoForm, setPromoForm] = useState({
    code: '',
    type: 'percent',
    value: '',
    maxUses: '',
    maxUsesPerMember: '',
    active: true,
    startsAt: '',
    expiresAt: '',
  });
  const [selectedPromo, setSelectedPromo] = useState(null);
  const [promoEdit, setPromoEdit] = useState({
    value: '',
    maxUses: '',
    maxUsesPerMember: '',
    active: true,
    startsAt: '',
    expiresAt: '',
  });
  const [promoBusy, setPromoBusy] = useState(false);
  const [promoMsg, setPromoMsg] = useState(null);
  const [promoErr, setPromoErr] = useState(null);

  const [boutiqueStats, setBoutiqueStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsError, setStatsError] = useState(null);

  const loadProducts = useCallback(async () => {
    setProductsLoading(true);
    setProductsError(null);
    try {
      const res = await boutiqueApi.getProducts();
      setProducts(extractProducts(res));
    } catch (err) {
      setProducts([]);
      setProductsError(humanError(err));
    } finally {
      setProductsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  const loadPromos = useCallback(async () => {
    setPromosLoading(true);
    setPromosError(null);
    try {
      const res = await boutiqueApi.getPromos();
      setPromos(extractPromos(res));
    } catch (err) {
      setPromos([]);
      setPromosError(humanError(err));
    } finally {
      setPromosLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tab === 'promos') loadPromos();
  }, [tab, loadPromos]);

  const loadBoutiqueStats = useCallback(async () => {
    setStatsLoading(true);
    setStatsError(null);
    try {
      const res = await boutiqueApi.getBoutiqueStats();
      // Accept either { data: {...} } or flat object with products/orders/...
      const payload = res?.data && (res.data.products || res.data.orders) ? res.data : res;
      setBoutiqueStats(payload && typeof payload === 'object' ? payload : null);
    } catch (err) {
      setBoutiqueStats(null);
      setStatsError(humanError(err));
    } finally {
      setStatsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tab === 'overview' || tab === 'stats') loadBoutiqueStats();
  }, [tab, loadBoutiqueStats]);

  const categoriesFromProducts = useMemo(() => {
    const set = new Set();
    products.forEach((p) => {
      if (p?.category) set.add(String(p.category));
    });
    return Array.from(set).sort();
  }, [products]);

  const promoSegments = useMemo(() => {
    const active = promos.filter((p) => promoIsActive(p)).length;
    const inactive = promos.length - active;
    return [
      { label: 'Actives', value: active, color: '#10b981' },
      { label: 'Inactives', value: inactive, color: '#94a3b8' },
    ];
  }, [promos]);

  const filteredProducts = useMemo(() => {
    return products.filter((p) => {
      const title = productTitle(p).toLowerCase();
      const cat = String(p?.category || '').toLowerCase();
      const matchSearch =
        !search ||
        title.includes(search.toLowerCase()) ||
        cat.includes(search.toLowerCase()) ||
        String(p?.id || '').toLowerCase().includes(search.toLowerCase());
      const st = productStatus(p);
      const matchStatus = statusFilter === 'ALL' || st === statusFilter;
      const matchCat = categoryFilter === 'ALL' || p?.category === categoryFilter;
      return matchSearch && matchStatus && matchCat;
    });
  }, [products, search, statusFilter, categoryFilter]);

  const runCreate = async () => {
    setFormError(null);
    setFormSuccess(null);
    const title = createForm.title.trim();
    const category = createForm.category.trim();
    const priceDsi = Number(createForm.priceDsi);
    if (!title || !category || Number.isNaN(priceDsi)) {
      setFormError('Renseignez le titre, la catégorie et le prix.');
      return;
    }
    setBusy(true);
    try {
      await boutiqueApi.createProduct({ title, category, priceDsi });
      setFormSuccess('Produit créé.');
      setCreateForm({ title: '', category: '', priceDsi: '' });
      await loadProducts();
    } catch (err) {
      setFormError(humanError(err));
    } finally {
      setBusy(false);
    }
  };

  const openEdit = (p) => {
    setSelectedProduct(p);
    setEditing(true);
    setEditForm({
      title: productTitle(p) === '—' ? '' : productTitle(p),
      category: p?.category || '',
      priceDsi: String(productPrice(p) || ''),
      status: productStatus(p) || '',
    });
    setFormError(null);
    setFormSuccess(null);
  };

  const runUpdate = async () => {
    if (!selectedProduct?.id) return;
    const payload = {};
    if (editForm.title.trim()) payload.title = editForm.title.trim();
    if (editForm.category.trim()) payload.category = editForm.category.trim();
    if (editForm.priceDsi !== '') {
      const n = Number(editForm.priceDsi);
      if (!Number.isNaN(n)) payload.priceDsi = n;
    }
    if (editForm.status.trim()) payload.status = editForm.status.trim();
    if (!Object.keys(payload).length) {
      setFormError('Renseignez au moins un champ à mettre à jour.');
      return;
    }
    setBusy(true);
    setFormError(null);
    try {
      const res = await boutiqueApi.updateProduct(selectedProduct.id, payload);
      setFormSuccess('Produit mis à jour.');
      setSelectedProduct(res && typeof res === 'object' ? { ...selectedProduct, ...res } : selectedProduct);
      setEditing(false);
      await loadProducts();
    } catch (err) {
      setFormError(humanError(err));
    } finally {
      setBusy(false);
    }
  };

  const runDelete = async () => {
    if (!selectedProduct?.id) return;
    if (!window.confirm('Supprimer ce produit ? Cette action peut être refusée s’il possède un historique.')) return;
    setBusy(true);
    setFormError(null);
    try {
      await boutiqueApi.deleteProduct(selectedProduct.id);
      setFormSuccess('Produit supprimé.');
      setSelectedProduct(null);
      setEditing(false);
      await loadProducts();
    } catch (err) {
      setFormError(humanError(err));
    } finally {
      setBusy(false);
    }
  };

  const runCreatePromo = async () => {
    setPromoErr(null);
    setPromoMsg(null);
    const code = promoForm.code.trim();
    if (!code) {
      setPromoErr('Le code est obligatoire.');
      return;
    }
    const payload = { code, type: promoForm.type };
    if (promoForm.value !== '') payload.value = Number(promoForm.value);
    if (promoForm.maxUses !== '') payload.maxUses = Number(promoForm.maxUses);
    if (promoForm.maxUsesPerMember !== '') payload.maxUsesPerMember = Number(promoForm.maxUsesPerMember);
    payload.active = Boolean(promoForm.active);
    if (promoForm.startsAt.trim()) payload.startsAt = promoForm.startsAt.trim();
    if (promoForm.expiresAt.trim()) payload.expiresAt = promoForm.expiresAt.trim();
    setPromoBusy(true);
    try {
      await boutiqueApi.createPromo(payload);
      setPromoMsg('Promotion créée.');
      setPromoForm({
        code: '',
        type: 'percent',
        value: '',
        maxUses: '',
        maxUsesPerMember: '',
        active: true,
        startsAt: '',
        expiresAt: '',
      });
      await loadPromos();
    } catch (err) {
      setPromoErr(humanError(err));
    } finally {
      setPromoBusy(false);
    }
  };

  const openPromoEdit = (p) => {
    setSelectedPromo(p);
    setPromoEdit({
      value: p?.value != null ? String(p.value) : '',
      maxUses: promoMaxUses(p) != null ? String(promoMaxUses(p)) : '',
      maxUsesPerMember: p?.maxUsesPerMember != null ? String(p.maxUsesPerMember) : '',
      active: promoIsActive(p),
      startsAt: p?.startsAt || p?.starts_at || '',
      expiresAt: p?.expiresAt || p?.expires_at || '',
    });
    setPromoErr(null);
    setPromoMsg(null);
  };

  const runUpdatePromo = async () => {
    if (!selectedPromo?.code) return;
    const payload = {};
    if (promoEdit.value !== '') payload.value = Number(promoEdit.value);
    if (promoEdit.maxUses !== '') payload.maxUses = Number(promoEdit.maxUses);
    if (promoEdit.maxUsesPerMember !== '') payload.maxUsesPerMember = Number(promoEdit.maxUsesPerMember);
    payload.active = Boolean(promoEdit.active);
    if (promoEdit.startsAt.trim()) payload.startsAt = promoEdit.startsAt.trim();
    if (promoEdit.expiresAt.trim()) payload.expiresAt = promoEdit.expiresAt.trim();
    setPromoBusy(true);
    setPromoErr(null);
    try {
      await boutiqueApi.updatePromo(selectedPromo.code, payload);
      setPromoMsg('Promotion mise à jour.');
      setSelectedPromo(null);
      await loadPromos();
    } catch (err) {
      setPromoErr(humanError(err));
    } finally {
      setPromoBusy(false);
    }
  };


  return (
    <div className="space-y-6 fade-up">
      <PageHeader
        title={t('pages.boutique.title')}
        subtitle={t('pages.boutique.subtitle')}
        actions={
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={loadProducts} className="dsi-btn-secondary text-xs" disabled={productsLoading}>
              <RefreshCw size={14} className={productsLoading ? 'animate-spin' : ''} /> Actualiser
            </button>
            {writeOk && (
              <button type="button" onClick={() => setTab('create')} className="dsi-btn-primary text-xs">
                <Plus size={14} /> Nouveau produit
              </button>
            )}
          </div>
        }
      />

      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[11px] text-slate-500 bg-slate-100 border border-slate-200 px-2 py-1 rounded-full">
          Distinct du Marketplace (vendeurs membres)
        </span>
      </div>

      <div className="flex flex-wrap gap-1 bg-white border border-slate-200 p-1.5 rounded-2xl">
        {TABS.map((item) => {
          const Icon = item.icon;
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => setTab(item.id)}
              className={`px-3 py-2 rounded-xl text-xs font-semibold flex items-center gap-1.5 transition-all ${
                tab === item.id ? 'bg-indigo-600 text-white shadow-sm' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-50'
              }`}
            >
              <Icon size={14} /> {item.label}
            </button>
          );
        })}
      </div>

      {tab === 'overview' && (
        <div className="space-y-4">
          {statsError && (
            <div className="p-3 rounded-2xl bg-rose-50 border border-rose-200 text-rose-800 text-xs font-bold flex justify-between gap-2">
              <span className="flex items-center gap-2"><ShieldAlert size={14} /> {statsError}</span>
              <button type="button" className="underline" onClick={loadBoutiqueStats}>Réessayer</button>
            </div>
          )}
          <div className="flex justify-end">
            <button type="button" className="dsi-btn-secondary text-xs" onClick={loadBoutiqueStats} disabled={statsLoading}>
              <RefreshCw size={14} className={statsLoading ? 'animate-spin' : ''} /> Actualiser les statistiques
            </button>
          </div>
          {statsLoading && !boutiqueStats ? (
            <div className="dsi-card p-10 text-center text-xs text-slate-500">Chargement des statistiques…</div>
          ) : !boutiqueStats ? (
            <EmptyState title="Statistiques indisponibles" message="Aucune statistique boutique n’a pu être chargée." onRetry={loadBoutiqueStats} />
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-5 gap-3">
                {[
                  { label: 'Produits', value: boutiqueStats.products?.total },
                  { label: 'Brouillons', value: boutiqueStats.products?.draft },
                  { label: 'Publiés', value: boutiqueStats.products?.published },
                  { label: 'Archivés', value: boutiqueStats.products?.archived },
                  { label: 'Commandes', value: boutiqueStats.orders?.total },
                  { label: 'En validation', value: boutiqueStats.orders?.validating },
                  { label: 'Attente paiement', value: boutiqueStats.orders?.pendingPayment },
                  { label: 'En traitement', value: boutiqueStats.orders?.processing },
                  { label: 'Payées', value: boutiqueStats.orders?.paid },
                  { label: 'Échouées', value: boutiqueStats.orders?.failed },
                  { label: 'Paiement inconnu', value: boutiqueStats.orders?.paymentUnknown },
                  { label: 'Cmd. payées (ventes)', value: boutiqueStats.sales?.paidOrders },
                  { label: 'Total DSI', value: boutiqueStats.sales?.totalDsi },
                  { label: 'Accès', value: boutiqueStats.entitlements?.total },
                  { label: 'Accès non résolus', value: boutiqueStats.entitlements?.unresolved },
                  { label: 'Promotions', value: boutiqueStats.promos?.total },
                  { label: 'Promos utilisables', value: boutiqueStats.promos?.currentlyUsable },
                  { label: 'Utilisations promo', value: boutiqueStats.promos?.totalRedemptions },
                  { label: 'Remises DSI', value: boutiqueStats.promos?.totalDiscountDsi },
                ].map((k) => (
                  <div key={k.label} className="dsi-card p-4">
                    <p className="text-[10px] font-semibold uppercase text-slate-500">{k.label}</p>
                    <p className="text-xl font-bold text-slate-900 mt-1">
                      {k.value == null ? '—' : typeof k.value === 'number' ? k.value.toLocaleString('fr-FR') : String(k.value)}
                    </p>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                <div className="dsi-card p-5">
                  <h3 className="text-sm font-semibold text-slate-900">État du catalogue</h3>
                  <p className="text-xs text-slate-500 mb-3">Brouillon · Publié · Archivé</p>
                  <DonutChart
                    segments={[
                      { label: 'Brouillons', value: Number(boutiqueStats.products?.draft) || 0, color: '#f59e0b' },
                      { label: 'Publiés', value: Number(boutiqueStats.products?.published) || 0, color: '#10b981' },
                      { label: 'Archivés', value: Number(boutiqueStats.products?.archived) || 0, color: '#94a3b8' },
                    ]}
                    centerValue={boutiqueStats.products?.total ?? '—'}
                    centerLabel="Produits"
                  />
                </div>
                <div className="dsi-card p-5">
                  <h3 className="text-sm font-semibold text-slate-900">État des commandes</h3>
                  <p className="text-xs text-slate-500 mb-3">Statuts fournis par le service</p>
                  <DonutChart
                    segments={[
                      { label: 'Validation', value: Number(boutiqueStats.orders?.validating) || 0, color: '#0ea5e9' },
                      { label: 'Attente paiement', value: Number(boutiqueStats.orders?.pendingPayment) || 0, color: '#f59e0b' },
                      { label: 'Traitement', value: Number(boutiqueStats.orders?.processing) || 0, color: '#8b5cf6' },
                      { label: 'Payées', value: Number(boutiqueStats.orders?.paid) || 0, color: '#10b981' },
                      { label: 'Échouées', value: Number(boutiqueStats.orders?.failed) || 0, color: '#f43f5e' },
                      { label: 'Paiement inconnu', value: Number(boutiqueStats.orders?.paymentUnknown) || 0, color: '#64748b' },
                    ]}
                    centerValue={boutiqueStats.orders?.total ?? '—'}
                    centerLabel="Commandes"
                  />
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {tab === 'products' && (
        <div className="space-y-4">
          {productsError && (
            <div className="p-3 rounded-2xl bg-rose-50 border border-rose-200 text-rose-800 text-xs font-bold flex justify-between gap-2">
              <span className="flex items-center gap-2"><ShieldAlert size={14} /> {productsError}</span>
              <button type="button" className="underline" onClick={loadProducts}>Réessayer</button>
            </div>
          )}
          <div className="dsi-card p-4 flex flex-col lg:flex-row gap-3 items-center justify-between">
            <div className="relative w-full lg:flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input className="dsi-input pl-9" placeholder="Rechercher un produit…" value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
            <div className="flex flex-wrap gap-2">
              <select className="dsi-input w-auto" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="ALL">Tous les statuts</option>
                <option value="published">Publié</option>
                <option value="draft">Brouillon</option>
                <option value="archived">Archivé</option>
                <option value="active">Actif</option>
                <option value="inactive">Inactif</option>
              </select>
              <select className="dsi-input w-auto" value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
                <option value="ALL">Toutes catégories</option>
                {categoriesFromProducts.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
              <button type="button" onClick={() => setViewMode('cards')} className={`px-3 py-2 rounded-xl text-xs font-semibold border ${viewMode === 'cards' ? 'bg-slate-900 text-white' : 'bg-white border-slate-200 text-slate-600'}`}>Cartes</button>
              <button type="button" onClick={() => setViewMode('table')} className={`px-3 py-2 rounded-xl text-xs font-semibold border ${viewMode === 'table' ? 'bg-slate-900 text-white' : 'bg-white border-slate-200 text-slate-600'}`}>Tableau</button>
            </div>
          </div>

          {productsLoading ? (
            <div className="dsi-card p-10 text-center text-xs text-slate-500">Chargement des produits…</div>
          ) : filteredProducts.length === 0 ? (
            <EmptyState title="Aucun produit" message={products.length === 0 ? 'Le catalogue ne contient aucun produit.' : 'Aucun produit ne correspond aux filtres.'} onRetry={loadProducts} />
          ) : viewMode === 'cards' ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
              {filteredProducts.map((p) => (
                <button key={p.id} type="button" onClick={() => { setSelectedProduct(p); setEditing(false); setFormError(null); }} className="dsi-card p-4 text-left hover:border-indigo-200 transition-all">
                  <div className="h-28 rounded-xl bg-gradient-to-br from-indigo-50 to-slate-100 border border-slate-200 flex items-center justify-center mb-3">
                    <Package size={28} className="text-indigo-400" />
                  </div>
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-sm font-semibold text-slate-900">{productTitle(p)}</p>
                    {productStatusBadge(productStatus(p))}
                  </div>
                  <p className="text-xs text-slate-500 mt-1">{p.category || '—'}</p>
                  <p className="text-sm font-bold text-slate-900 mt-2">{productPrice(p).toLocaleString('fr-FR')} FCFA</p>
                </button>
              ))}
            </div>
          ) : (
            <div className="dsi-card overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead className="bg-slate-50 text-[11px] uppercase text-slate-500 font-semibold border-b border-slate-200">
                    <tr>
                      <th className="px-4 py-3">Produit</th>
                      <th className="px-4 py-3">Catégorie</th>
                      <th className="px-4 py-3">Prix</th>
                      <th className="px-4 py-3">Statut</th>
                      <th className="px-4 py-3">Créé le</th>
                      <th className="px-4 py-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filteredProducts.map((p) => (
                      <tr key={p.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3 font-semibold text-slate-900">{productTitle(p)}</td>
                        <td className="px-4 py-3 text-slate-600">{p.category || '—'}</td>
                        <td className="px-4 py-3 font-bold">{productPrice(p).toLocaleString('fr-FR')} FCFA</td>
                        <td className="px-4 py-3">{productStatusBadge(productStatus(p))}</td>
                        <td className="px-4 py-3 text-xs text-slate-500">{productCreated(p)}</td>
                        <td className="px-4 py-3 text-right">
                          <button type="button" onClick={() => { setSelectedProduct(p); setEditing(false); setFormError(null); }} className="dsi-btn-secondary text-[11px] py-1.5">
                            <Eye size={12} /> Voir
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {tab === 'create' && (
        <div className="dsi-card p-6 space-y-4 max-w-3xl">
          <div>
            <h3 className="text-sm font-semibold text-slate-900">Nouveau produit</h3>
            <p className="text-xs text-slate-500">Titre, catégorie et prix sont obligatoires.</p>
          </div>
          {formError && (
            <div className="p-3 rounded-2xl bg-rose-50 border border-rose-200 text-rose-800 text-xs font-bold flex gap-2">
              <ShieldAlert size={14} /> {formError}
            </div>
          )}
          {formSuccess && (
            <div className="p-3 rounded-2xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-bold flex gap-2">
              <CheckCircle2 size={14} /> {formSuccess}
            </div>
          )}
          {!writeOk && (
            <div className="p-3 rounded-2xl bg-amber-50 border border-amber-200 text-amber-800 text-xs font-bold">
              Vous n’avez pas l’autorisation de créer un produit.
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] font-semibold text-slate-500 uppercase">Titre</label>
              <input className="dsi-input mt-1" value={createForm.title} onChange={(e) => setCreateForm({ ...createForm, title: e.target.value })} disabled={!writeOk || busy} />
            </div>
            <div>
              <label className="text-[11px] font-semibold text-slate-500 uppercase">Catégorie</label>
              <input className="dsi-input mt-1" value={createForm.category} onChange={(e) => setCreateForm({ ...createForm, category: e.target.value })} disabled={!writeOk || busy} placeholder="Ex. Formations" />
            </div>
            <div>
              <label className="text-[11px] font-semibold text-slate-500 uppercase">Prix (FCFA)</label>
              <input className="dsi-input mt-1" type="number" min={0} value={createForm.priceDsi} onChange={(e) => setCreateForm({ ...createForm, priceDsi: e.target.value })} disabled={!writeOk || busy} />
            </div>
          </div>
          <button type="button" className="dsi-btn-primary text-xs" disabled={!writeOk || busy} onClick={runCreate}>
            <Plus size={14} /> {busy ? 'Création…' : 'Créer le produit'}
          </button>
        </div>
      )}

      {tab === 'promos' && (
        <div className="space-y-4">
          {promosError && (
            <div className="p-3 rounded-2xl bg-rose-50 border border-rose-200 text-rose-800 text-xs font-bold flex justify-between gap-2">
              <span className="flex items-center gap-2"><ShieldAlert size={14} /> {promosError}</span>
              <button type="button" className="underline" onClick={loadPromos}>Réessayer</button>
            </div>
          )}
          {promoErr && (
            <div className="p-3 rounded-2xl bg-rose-50 border border-rose-200 text-rose-800 text-xs font-bold flex gap-2">
              <ShieldAlert size={14} /> {promoErr}
            </div>
          )}
          {promoMsg && (
            <div className="p-3 rounded-2xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-bold flex gap-2">
              <CheckCircle2 size={14} /> {promoMsg}
            </div>
          )}

          <div className="flex flex-wrap justify-between gap-2 items-center">
            <p className="text-xs text-slate-500">{promos.length} code{promos.length !== 1 ? 's' : ''} promo</p>
            <button type="button" className="dsi-btn-secondary text-xs" onClick={loadPromos} disabled={promosLoading}>
              <RefreshCw size={14} className={promosLoading ? 'animate-spin' : ''} /> Actualiser
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="dsi-card p-4">
              <p className="text-[10px] uppercase text-slate-500 font-semibold">Actives</p>
              <p className="text-xl font-bold mt-1">{promos.filter((p) => promoIsActive(p)).length}</p>
            </div>
            <div className="dsi-card p-4">
              <p className="text-[10px] uppercase text-slate-500 font-semibold">Inactives</p>
              <p className="text-xl font-bold mt-1">{promos.filter((p) => !promoIsActive(p)).length}</p>
            </div>
          </div>

          {promosWriteOk && (
            <div className="dsi-card p-5 space-y-3 max-w-3xl">
              <h3 className="text-sm font-bold">Créer une promotion</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                <label className="space-y-1">
                  <span className="font-semibold text-slate-600">Code</span>
                  <input className="dsi-input" value={promoForm.code} onChange={(e) => setPromoForm({ ...promoForm, code: e.target.value })} disabled={promoBusy} />
                </label>
                <label className="space-y-1">
                  <span className="font-semibold text-slate-600">Type</span>
                  <select className="dsi-input" value={promoForm.type} onChange={(e) => setPromoForm({ ...promoForm, type: e.target.value })} disabled={promoBusy}>
                    <option value="percent">Pourcentage</option>
                    <option value="fixed">Montant fixe</option>
                  </select>
                </label>
                <label className="space-y-1">
                  <span className="font-semibold text-slate-600">Valeur (optionnel)</span>
                  <input className="dsi-input" type="number" value={promoForm.value} onChange={(e) => setPromoForm({ ...promoForm, value: e.target.value })} disabled={promoBusy} />
                </label>
                <label className="space-y-1">
                  <span className="font-semibold text-slate-600">Utilisations max (optionnel)</span>
                  <input className="dsi-input" type="number" value={promoForm.maxUses} onChange={(e) => setPromoForm({ ...promoForm, maxUses: e.target.value })} disabled={promoBusy} />
                </label>
                <label className="space-y-1">
                  <span className="font-semibold text-slate-600">Max par membre (optionnel)</span>
                  <input className="dsi-input" type="number" value={promoForm.maxUsesPerMember} onChange={(e) => setPromoForm({ ...promoForm, maxUsesPerMember: e.target.value })} disabled={promoBusy} />
                </label>
                <label className="space-y-1 flex items-center gap-2 pt-5">
                  <input type="checkbox" checked={promoForm.active} onChange={(e) => setPromoForm({ ...promoForm, active: e.target.checked })} disabled={promoBusy} />
                  <span className="font-semibold text-slate-600">Active</span>
                </label>
                <label className="space-y-1">
                  <span className="font-semibold text-slate-600">Début (optionnel)</span>
                  <input className="dsi-input" type="datetime-local" value={promoForm.startsAt} onChange={(e) => setPromoForm({ ...promoForm, startsAt: e.target.value })} disabled={promoBusy} />
                </label>
                <label className="space-y-1">
                  <span className="font-semibold text-slate-600">Fin (optionnel)</span>
                  <input className="dsi-input" type="datetime-local" value={promoForm.expiresAt} onChange={(e) => setPromoForm({ ...promoForm, expiresAt: e.target.value })} disabled={promoBusy} />
                </label>
              </div>
              <button type="button" className="dsi-btn-primary text-xs" disabled={promoBusy} onClick={runCreatePromo}>
                {promoBusy ? 'Création…' : 'Créer la promotion'}
              </button>
            </div>
          )}

          {promosLoading ? (
            <div className="dsi-card p-10 text-center text-xs text-slate-500">Chargement des promotions…</div>
          ) : !promos.length ? (
            <EmptyState title="Aucune promotion" message="Aucun code promo n’est enregistré." onRetry={loadPromos} />
          ) : (
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
              <div className="dsi-card p-5">
                <h3 className="text-sm font-semibold mb-3">Répartition</h3>
                <DonutChart segments={promoSegments} centerValue={promos.length} centerLabel="Codes" />
              </div>
              <div className="space-y-2">
                {promos.map((p) => {
                  const uses = promoUses(p);
                  const maxU = promoMaxUses(p);
                  const usage =
                    uses != null && maxU != null
                      ? `${uses} / ${maxU} utilisations`
                      : uses != null
                        ? `${uses} utilisation${uses !== 1 ? 's' : ''}`
                        : maxU != null
                          ? `Max ${maxU}`
                          : null;
                  return (
                    <div key={p.code || p.id} className="dsi-card p-4 flex items-center justify-between gap-2">
                      <div>
                        <p className="font-semibold text-sm font-mono">{p.code}</p>
                        <p className="text-xs text-slate-500">
                          {p.type === 'percent' ? 'Pourcentage' : p.type === 'fixed' ? 'Montant fixe' : p.type || '—'}
                          {p.value != null ? ` · ${p.value}` : ''}
                          {usage ? ` · ${usage}` : ''}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <StatusBadge
                          statusKey={promoIsActive(p) ? 'ACTIF' : 'INACTIF'}
                          text={promoIsActive(p) ? 'Active' : 'Inactive'}
                        />
                        {promosWriteOk && (
                          <button type="button" className="dsi-btn-secondary text-[11px] py-1.5" onClick={() => openPromoEdit(p)}>
                            Modifier
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {selectedPromo && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
              <div className="absolute inset-0 bg-slate-900/40" onClick={() => !promoBusy && setSelectedPromo(null)} />
              <div className="relative w-full max-w-md dsi-card p-6 space-y-3">
                <h3 className="font-bold">Modifier {selectedPromo.code}</h3>
                <p className="text-[11px] text-slate-500">Le code lui-même n’est pas modifiable ici.</p>
                <label className="block text-xs space-y-1">
                  <span className="font-semibold">Valeur</span>
                  <input className="dsi-input" type="number" value={promoEdit.value} onChange={(e) => setPromoEdit({ ...promoEdit, value: e.target.value })} />
                </label>
                <label className="block text-xs space-y-1">
                  <span className="font-semibold">Utilisations max</span>
                  <input className="dsi-input" type="number" value={promoEdit.maxUses} onChange={(e) => setPromoEdit({ ...promoEdit, maxUses: e.target.value })} />
                </label>
                <label className="block text-xs space-y-1">
                  <span className="font-semibold">Max par membre</span>
                  <input className="dsi-input" type="number" value={promoEdit.maxUsesPerMember} onChange={(e) => setPromoEdit({ ...promoEdit, maxUsesPerMember: e.target.value })} />
                </label>
                <label className="flex items-center gap-2 text-xs">
                  <input type="checkbox" checked={promoEdit.active} onChange={(e) => setPromoEdit({ ...promoEdit, active: e.target.checked })} />
                  Active
                </label>
                <label className="block text-xs space-y-1">
                  <span className="font-semibold">Début</span>
                  <input className="dsi-input" type="datetime-local" value={promoEdit.startsAt} onChange={(e) => setPromoEdit({ ...promoEdit, startsAt: e.target.value })} />
                </label>
                <label className="block text-xs space-y-1">
                  <span className="font-semibold">Fin</span>
                  <input className="dsi-input" type="datetime-local" value={promoEdit.expiresAt} onChange={(e) => setPromoEdit({ ...promoEdit, expiresAt: e.target.value })} />
                </label>
                <div className="flex justify-end gap-2">
                  <button type="button" className="dsi-btn-secondary text-xs" disabled={promoBusy} onClick={() => setSelectedPromo(null)}>Annuler</button>
                  <button type="button" className="dsi-btn-primary text-xs" disabled={promoBusy} onClick={runUpdatePromo}>{promoBusy ? '…' : 'Enregistrer'}</button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {tab === 'stats' && (
        <div className="space-y-4">
          {statsError && (
            <div className="p-3 rounded-2xl bg-rose-50 border border-rose-200 text-rose-800 text-xs font-bold flex justify-between gap-2">
              <span className="flex items-center gap-2"><ShieldAlert size={14} /> {statsError}</span>
              <button type="button" className="underline" onClick={loadBoutiqueStats}>Réessayer</button>
            </div>
          )}
          <div className="flex justify-end">
            <button type="button" className="dsi-btn-secondary text-xs" onClick={loadBoutiqueStats} disabled={statsLoading}>
              <RefreshCw size={14} className={statsLoading ? 'animate-spin' : ''} /> Actualiser
            </button>
          </div>
          {statsLoading && !boutiqueStats ? (
            <div className="dsi-card p-10 text-center text-xs text-slate-500">Chargement des statistiques…</div>
          ) : !boutiqueStats ? (
            <EmptyState title="Statistiques indisponibles" message="Aucune statistique boutique n’a pu être chargée." onRetry={loadBoutiqueStats} />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="dsi-card p-5">
                <h3 className="text-sm font-semibold text-slate-900 mb-3">Produits</h3>
                <DonutChart
                  segments={[
                    { label: 'Brouillons', value: Number(boutiqueStats.products?.draft) || 0, color: '#f59e0b' },
                    { label: 'Publiés', value: Number(boutiqueStats.products?.published) || 0, color: '#10b981' },
                    { label: 'Archivés', value: Number(boutiqueStats.products?.archived) || 0, color: '#94a3b8' },
                  ]}
                  centerValue={boutiqueStats.products?.total ?? '—'}
                  centerLabel="Total"
                />
              </div>
              <div className="dsi-card p-5">
                <h3 className="text-sm font-semibold text-slate-900 mb-3">Commandes</h3>
                <DonutChart
                  segments={[
                    { label: 'Validation', value: Number(boutiqueStats.orders?.validating) || 0, color: '#0ea5e9' },
                    { label: 'Attente paiement', value: Number(boutiqueStats.orders?.pendingPayment) || 0, color: '#f59e0b' },
                    { label: 'Traitement', value: Number(boutiqueStats.orders?.processing) || 0, color: '#8b5cf6' },
                    { label: 'Payées', value: Number(boutiqueStats.orders?.paid) || 0, color: '#10b981' },
                    { label: 'Échouées', value: Number(boutiqueStats.orders?.failed) || 0, color: '#f43f5e' },
                    { label: 'Paiement inconnu', value: Number(boutiqueStats.orders?.paymentUnknown) || 0, color: '#64748b' },
                  ]}
                  centerValue={boutiqueStats.orders?.total ?? '—'}
                  centerLabel="Total"
                />
              </div>
              <div className="dsi-card p-5 space-y-2">
                <h3 className="text-sm font-semibold text-slate-900">Ventes</h3>
                <p className="text-xs text-slate-500">Commandes payées : <strong>{boutiqueStats.sales?.paidOrders ?? '—'}</strong></p>
                <p className="text-xs text-slate-500">Total DSI : <strong>{boutiqueStats.sales?.totalDsi != null ? Number(boutiqueStats.sales.totalDsi).toLocaleString('fr-FR') : '—'}</strong></p>
              </div>
              <div className="dsi-card p-5 space-y-2">
                <h3 className="text-sm font-semibold text-slate-900">Accès & promotions</h3>
                <p className="text-xs text-slate-500">Accès : <strong>{boutiqueStats.entitlements?.total ?? '—'}</strong> · Non résolus : <strong>{boutiqueStats.entitlements?.unresolved ?? '—'}</strong></p>
                <p className="text-xs text-slate-500">Promos : <strong>{boutiqueStats.promos?.total ?? '—'}</strong> · Utilisables : <strong>{boutiqueStats.promos?.currentlyUsable ?? '—'}</strong></p>
                <p className="text-xs text-slate-500">Utilisations : <strong>{boutiqueStats.promos?.totalRedemptions ?? '—'}</strong> · Remises DSI : <strong>{boutiqueStats.promos?.totalDiscountDsi != null ? Number(boutiqueStats.promos.totalDiscountDsi).toLocaleString('fr-FR') : '—'}</strong></p>
              </div>
            </div>
          )}
        </div>
      )}

      {selectedProduct && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/40" onClick={() => !busy && setSelectedProduct(null)} />
          <div className="relative w-full max-w-lg dsi-card p-6 space-y-4">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-base font-bold text-slate-900">{productTitle(selectedProduct)}</h3>
                <p className="text-xs text-slate-500 font-mono">{selectedProduct.id}</p>
              </div>
              <button type="button" onClick={() => setSelectedProduct(null)} className="p-2 rounded-xl hover:bg-slate-100"><X size={18} /></button>
            </div>
            {formError && (
              <div className="p-2 rounded-xl bg-rose-50 border border-rose-200 text-rose-800 text-xs font-bold">{formError}</div>
            )}
            {formSuccess && (
              <div className="p-2 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-bold">{formSuccess}</div>
            )}
            {!editing ? (
              <>
                <div className="grid grid-cols-2 gap-3 text-xs">
                  {[
                    ['Catégorie', selectedProduct.category || '—'],
                    ['Prix', `${productPrice(selectedProduct).toLocaleString('fr-FR')} FCFA`],
                    ['Statut', productStatus(selectedProduct) || '—'],
                    ['Créé le', productCreated(selectedProduct)],
                  ].map(([k, v]) => (
                    <div key={k} className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                      <p className="text-[10px] uppercase text-slate-400 font-semibold">{k}</p>
                      <p className="mt-1 font-medium text-slate-800">{v}</p>
                    </div>
                  ))}
                </div>
                {(selectedProduct.summary || selectedProduct.description) && (
                  <p className="text-xs text-slate-600">{selectedProduct.summary || selectedProduct.description}</p>
                )}
                {writeOk && (
                  <div className="flex flex-wrap gap-2 justify-end">
                    <button type="button" className="dsi-btn-secondary text-xs" onClick={() => openEdit(selectedProduct)} disabled={busy}>
                      <Pencil size={12} /> Modifier
                    </button>
                    <button type="button" className="dsi-btn-secondary text-xs text-rose-700" onClick={runDelete} disabled={busy}>
                      <Trash2 size={12} /> Supprimer
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="space-y-3 text-xs">
                <label className="block space-y-1">
                  <span className="font-semibold">Titre</span>
                  <input className="dsi-input" value={editForm.title} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} />
                </label>
                <label className="block space-y-1">
                  <span className="font-semibold">Catégorie</span>
                  <input className="dsi-input" value={editForm.category} onChange={(e) => setEditForm({ ...editForm, category: e.target.value })} />
                </label>
                <label className="block space-y-1">
                  <span className="font-semibold">Prix (FCFA)</span>
                  <input className="dsi-input" type="number" min={0} value={editForm.priceDsi} onChange={(e) => setEditForm({ ...editForm, priceDsi: e.target.value })} />
                </label>
                <label className="block space-y-1">
                  <span className="font-semibold">Statut</span>
                  <input className="dsi-input" value={editForm.status} onChange={(e) => setEditForm({ ...editForm, status: e.target.value })} placeholder="Selon le service boutique" />
                </label>
                <div className="flex justify-end gap-2">
                  <button type="button" className="dsi-btn-secondary text-xs" disabled={busy} onClick={() => setEditing(false)}>Annuler</button>
                  <button type="button" className="dsi-btn-primary text-xs" disabled={busy} onClick={runUpdate}>{busy ? '…' : 'Enregistrer'}</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

    </div>
  );
}
